<script setup>
import { computed, getCurrentInstance } from 'vue';
import FormSidebarBtn from "./FormSidebarBtn.vue";

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- Computed ---

const formState = computed(() => store.state[props.formId]);

const isArchived = computed(() => !!formState.value?.currentData?.isArchived);

const buttonText = computed(() => isArchived.value ? 'Remove from archive' : 'Add to archive');

const tableName = computed(() => formState.value?.tableName);

const id = computed(() => formState.value?.id);

const componentId = computed(() => store.state.components.componentIds[tableName.value]);

const hasAccess = computed(() => store.getters['user/access'](tableName.value, 'archive'));

const show = computed(() => {
    if (!id.value) {
        return false;
    }

    // The home page cannot be archived
    if (tableName.value === 'pages' && id.value === store.state.settings.homePage) {
        return false;
    }

    // Content should not be archived from the site tree, as this removes the content from all pages.
    // Users must archive it explicitly from the Content component.
    if (
        tableName.value === 'content' && window.location.hash.split('/')[1] === 'site_tree'
    ) {
        return false;
    }

    if (hasAccess.value) {
        const component = store.state.components.items.find(obj => obj.id === componentId.value);
        return !!component?.useArchive;
    }

    return false;
});

// --- Methods ---

const onClick = () => {
    const modalTitle = isArchived.value ? 'Remove from archive' : 'Add to archive';
    const modalActionText = isArchived.value ? 'remove this item from' : 'add this item to';

    store.dispatch('modals/show', {
        componentName: 'ConfirmModal',
        obj: {
            modalTitle: modalTitle,
            modalBody: `<p>Are you sure you wish to ${modalActionText} the archive?</p>`,
            onConfirm: () => {
                // The isArchived column stores 1|0, not true|false.
                const value = isArchived.value ? 0 : 1;

                store.dispatch('itemData/archive', {
                        tableName: tableName.value,
                        id: id.value,
                        doArchive: value,
                    })
                    .then(() => {
                        store.commit(`${props.formId}/presetData`, {
                            name: 'isArchived',
                            value: value,
                        });

                        // Refresh site tree pane
                        store.dispatch('siteTree/init');
                    })
                    .catch((error) => {
                        // silent catch
                    });
            }
        }
    });
};
</script>

<template>
    <FormSidebarBtn v-if="show" @click.prevent.native="onClick">
        <i class="me-2 bi-archive"/>
        {{ buttonText }}
    </FormSidebarBtn>
</template>